// ============================================================
// ActionPlate - one action in the drum.
//
// A deliberately simpler cousin of the armoury's WeaponPlate: an
// optional section caption worn like a fieldset legend, a name,
// advisory cost badge, one-line blurb, a CHAMBERED marker for the
// action loaded in the trigger, and a disabled state that SAYS WHY.
// Attacks wear the attack colour - the drum's way of saying which
// plate is the point of the whole cylinder. Geometry (the drum
// offsets) is the drum's business - the plate only renders.
// ============================================================
import type { CSSProperties } from 'react';
import type { ActionDef } from '../../engine/actions/types';
import { COST_LABELS } from '../../engine/actions/types';
import styles from '../../styles/ActionDrum.module.css';
import clsx from '../../utils/clsx';

interface Props {
  def: ActionDef;
  /** Section caption, on the FIRST plate of each run - a fieldset legend. */
  caption?: string;
  /** Signed drum offset - 0 is the cursor row. */
  offset: number;
  /** This action is loaded in the trigger. */
  chambered: boolean;
  /** This action's state is currently held - an engaged stance. */
  engaged?: boolean;
  /** Why it cannot chamber right now; falsy = chamberable. */
  disabledReason?: string | null;
  focused: boolean;
  onExecute: (id: string) => void;
}

const COST_CLASS: Record<string, string> = {
  half: 'costHalf',
  full: 'costFull',
  halfOrFull: 'costHalf',
  reaction: 'costReaction',
};

// Four notches each side since v8.300.0 - the cylinder holds enough
// actions now that a three-plate window hid whole sections.
const VISIBLE = 4;

/**
 * A plate's place on the cylinder: 42px a notch, shrinking and fading
 * with distance from the cursor row; beyond the window it is invisible
 * and takes no clicks. Load-bearing for E2E: the plates' offsets are
 * what `pressPlate` navigates by (gotcha 10).
 */
export function plateStyle(offset: number): CSSProperties {
  const distance = Math.min(Math.abs(offset), VISIBLE);
  const hidden = Math.abs(offset) > VISIBLE;
  return {
    transform: `translateY(${offset * 42}px) scale(${1 - distance * 0.05})`,
    opacity: hidden ? 0 : 1 - distance * 0.2,
    pointerEvents: hidden ? 'none' : undefined,
    zIndex: 10 - Math.abs(offset),
  };
}

export default function ActionPlate({
  def,
  caption,
  offset,
  chambered,
  engaged = false,
  disabledReason,
  focused,
  onExecute,
}: Props) {
  return (
    <button
      type="button"
      data-testid={`action-plate-${def.id}`}
      className={clsx(
        styles.plate,
        def.category === 'attack' && styles.attackPlate,
        (chambered || engaged) && styles.engaged,
        focused && styles.focused,
        disabledReason && styles.disabled,
      )}
      style={plateStyle(offset)}
      aria-pressed={chambered || engaged}
      aria-disabled={!!disabledReason}
      title={disabledReason || def.blurb}
      // ONE click chambers, wherever the plate sits (v8.238.0).
      // Chambering is harmless - nothing fires until the trigger - and
      // the old click-to-centre-first two-step had people clicking
      // straight through the closed drum into whatever was behind it.
      onClick={disabledReason ? undefined : () => onExecute(def.id)}
    >
      {caption && (
        <span className={styles.sectionCaption} aria-hidden="true">
          {caption}
        </span>
      )}
      <span className={styles.plateName}>{def.name}</span>
      <span className={clsx(styles.costBadge, styles[COST_CLASS[def.cost]])}>{COST_LABELS[def.cost]}</span>
      <span className={styles.plateBlurb}>{disabledReason || def.blurb}</span>
      <PlateTag chambered={chambered} engaged={engaged} />
    </button>
  );
}

/** CHAMBERED for the action loaded in the trigger; ENGAGED for a held stance that is not. */
function PlateTag({ chambered, engaged }: { chambered: boolean; engaged: boolean }) {
  if (chambered) return <span className={styles.engagedTag}>CHAMBERED</span>;
  return engaged ? <span className={styles.engagedTag}>ENGAGED</span> : null;
}
